import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const BannerSection = styled.section`
  padding: 8rem 2rem 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 7rem 1.5rem 2rem 1.5rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem 2rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  border: 3px solid rgba(255, 255, 255, 0.8);

  @media (max-width: 768px) {
    padding: 2rem 1.2rem;
    border-radius: 18px;
  }
`;

const Badge = styled(motion.div)`
  display: inline-block;
  padding: 0.5rem 1.2rem;
  background: #ff6b6b;
  color: white;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 1.2rem;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
`;

const Title = styled(motion.h1)`
  color: #ff6b6b;
  font-size: 2.8rem;
  margin: 0 0 1rem 0;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', Arial, sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled(motion.p)`
  color: #444;
  font-size: 1.25rem;
  line-height: 1.7;
  margin: 0 auto 2rem auto;
  max-width: 750px;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    line-height: 1.5;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%);
  color: white;
  border: none;
  padding: 0.9rem 2rem;
  border-radius: 30px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(255, 107, 107, 0.4);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const SecondaryButton = styled(motion.a)`
  background: #f1f2f6;
  color: #333;
  border: 2px solid #ddd;
  padding: 0.85rem 1.8rem;
  border-radius: 30px;
  font-weight: bold;
  font-size: 1.05rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #e4e7eb;
    border-color: #bbb;
  }
`;

const PageBanner = ({
  badge,
  title,
  subtitle,
  primaryText,
  primaryLink,
  onPrimaryClick,
  secondaryText,
  secondaryLink
}) => {
  return (
    <BannerSection>
      <Container>
        {badge && (
          <Badge
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            {badge}
          </Badge>
        )}
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {title}
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {subtitle}
        </Subtitle>
        <ActionGroup>
          {primaryText && (
            primaryLink ? (
              primaryLink.startsWith('http') ? (
                <PrimaryButton
                  as="a"
                  href={primaryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {primaryText}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  as={Link}
                  to={primaryLink}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {primaryText}
                </PrimaryButton>
              )
            ) : (
              <PrimaryButton
                onClick={onPrimaryClick}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {primaryText}
              </PrimaryButton>
            )
          )}
          {secondaryText && (
            secondaryLink.startsWith('http') ? (
              <SecondaryButton
                href={secondaryLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {secondaryText}
              </SecondaryButton>
            ) : (
              <SecondaryButton
                as={Link}
                to={secondaryLink}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {secondaryText}
              </SecondaryButton>
            )
          )}
        </ActionGroup>
      </Container>
    </BannerSection>
  );
};

export default PageBanner;
